// @react
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
// @types
import {
  GetNetworkSummaryQuery,
  PublicUserConnectionType,
} from 'lib/graphql/graphqlTypes'
// @graphQL
import { ApolloError } from '@apollo/client'
// @libraries
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
// @components
import Container from '@bit/atd.web.container'
import Grid from '@bit/atd.web.grid'
import Spacer from '@bit/atd.web.spacer'
// @module components
import Navigation from 'modules/onBoarding/components/_common/Navigation'
import QueryError from 'modules/common/components/Errors/QueryError'
import PublicUsersList from 'modules/common/components/PublicUserList'
import strings from 'lib/constants/strings'
import { Direction, NavigationContext } from 'modules/common/context/navigation'
import useNavigation, { SuccessAction } from '../../../hooks/useNavigation'
import { Slide } from '@material-ui/core'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxWidth: '768px',
    margin: '0 auto',
    '& form': {
      width: '100%',
    },
  },
  formContainer: {
    position: 'relative',
  },
  cards: {
    '& .MuiGrid-item': {
      [theme.breakpoints.down('sm')]: {
        '@media (min-width: 375px)': {
          flexGrow: 0,
          maxWidth: '50%',
          flexBasis: '50%',
        },
        '@media (min-width: 670px)': {
          flexGrow: 0,
          maxWidth: '33.333%',
          flexBasis: '33.333%',
        },
      },
    },
  },
  h2: {
    color: '#2E2D31',
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '32px',
  },
  text: {
    fontSize: '16px',
    lineHeight: '22px',
  },
  textLarge: {
    fontSize: '18px',
    lineHeight: '24px',
    marginBottom: '24px',
  },
  hide: {
    display: 'none',
  },
}))

type SelfProps = {
  data?: GetNetworkSummaryQuery
  isLoading?: boolean
  isError?: ApolloError | undefined
}
type PropsType = SelfProps

const NetworkCardList = ({
  data,
  isLoading,
  isError,
}: PropsType): ReactElement => {
  /******************************************
   * STATE
   ******************************************/
  const [, setIsSubmitting] = useState<boolean>(false)
  const [slidingIn, setSlidingIn] = useState<boolean>(false)
  const [destination, setDestination] = useState<string>('')
  const [direction, setDirection] = useState<Direction>(Direction.LEFT)

  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()
  const history = useHistory()
  const navigation = useContext(NavigationContext)

  useEffect(() => {
    setSlidingIn(true)
  }, [])

  const { successHandler } = useNavigation({
    setDestination,
    setDirection,
    setSlidingIn,
    setIsSubmitting,
    isFormDirty: false,
  })
  const [followingCount, setFollowingCount] = useState<number>(0)

  /******************************************
   * FUNCTIONS
   ******************************************/

  const handleSaveExit = () => {
    successHandler(SuccessAction.EXIT)
  }

  const handlePrevClick = () => {
    successHandler(SuccessAction.PREV)
  }

  const handleNextClick = () => {
    successHandler(SuccessAction.NEXT)
  }

  /******************************************
   * RENDER
   ******************************************/
  const recommended = (
    <PublicUsersList
      title={strings.PROFILE.NETWORK.SUGGESTED_USERS_TITLE}
      subtitle={`We've automatically added some people to your network. Keep
              building your network with our suggestions.`}
      queryInput={{
        pageSize: 12,
        page: 1,
        filters: {
          type: PublicUserConnectionType.RECOMMENDED,
        },
      }}
    />
  )
  const following = (
    <div className={followingCount === 0 ? classes.hide : ''}>
      <Typography className={classes.h2} component="h2">
        These Users are currently in your network
      </Typography>
      <PublicUsersList
        title={'These Users are currently in your network'}
        setCount={setFollowingCount}
        subtitle={' '}
        queryInput={{
          pageSize: 4,
          page: 1,
          filters: {
            type: PublicUserConnectionType.FOLLOWING,
          },
        }}
      />
    </div>
  )

  return (
    <>
      <Spacer size={32} fullWidth />

      <Container>
        <Grid className={classes.container} container>
          <Grid item xs={12}>
            <Typography className={classes.textLarge} component="p">
              {`Connect with leaders in the industry, professionals with a role
              similar to yours, and thousands of other ATD users. When you
              follow somebody, you'll be able to see what they are reading,
              learning, sharing and discussing.`}
            </Typography>
            {following}
            <Typography className={classes.h2} component="h2">
              Suggested for You
            </Typography>
          </Grid>
        </Grid>
      </Container>

      <Spacer size={32} fullWidth />

      {/* Cards */}
      <div className={classes.formContainer}>
        <Slide
          in={slidingIn}
          timeout={500}
          direction={navigation.direction}
          unmountOnExit
          onExited={() => {
            navigation.setDirection(direction)
            history.push(destination)
          }}
        >
          <div>
            <Container>
              <Grid className={classes.container} container>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {isError || (!data && !isLoading && <QueryError />)}
                    {recommended}
                  </Grid>
                </Grid>
              </Grid>
            </Container>
          </div>
        </Slide>

        <Navigation
          handleExitOnClick={() => handleSaveExit()}
          handlePrevOnClick={() => handlePrevClick()}
          handleNextOnClick={() => handleNextClick()}
          prevPage={strings.ONBOARDING.COMMUNICATION.HEADER.TITLE}
          nextPage={strings.ONBOARDING.TOPICS.HEADER.TITLE}
        />
      </div>
    </>
  )
}

export default NetworkCardList
